import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as DisableIcon,
} from '@mui/icons-material'
import Table, { Column } from '../../components/ui/Table'
import { useAreas, useCreateArea, useUpdateArea, useDeleteArea } from '../../hooks/api/useAreas'
import { useBookers } from '../../hooks/api/useBookers'
import { Area, CreateAreaForm } from '../../types'
import { format } from 'date-fns'

const AreaListPage = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedArea, setSelectedArea] = useState<Area | null>(null)
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formData, setFormData] = useState<CreateAreaForm>({
    booker_id: '',
    area_name: '',
  })
  const [mode, setMode] = useState<'create' | 'edit'>('create')

  const { data: areasData, isLoading } = useAreas(page, rowsPerPage)
  const { data: bookersData } = useBookers({ page: 1, limit: 100 })
  const createAreaMutation = useCreateArea()
  const updateAreaMutation = useUpdateArea()
  const deleteAreaMutation = useDeleteArea()

  const areas = areasData?.results || []
  const bookers = bookersData?.data || []

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, area: Area) => {
    setAnchorEl(event.currentTarget)
    setSelectedArea(area)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedArea(null)
  }

  const handleEditArea = () => {
    if (selectedArea) {
      setEditingAreaId(selectedArea._id)
      setFormError(null)
      setFormData({
        booker_id: typeof selectedArea.booker_id === 'string' ? selectedArea.booker_id : selectedArea.booker_id._id,
        area_name: selectedArea.area_name,
      })
      setMode('edit')
      setFormOpen(true)
    }
    handleMenuClose()
  }

  const handleDeleteArea = () => {
    if (selectedArea) {
      deleteAreaMutation.mutate(selectedArea._id)
    }
    handleMenuClose()
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setSelectedArea(null)
    setEditingAreaId(null)
    setFormError(null)
    setFormData({
      booker_id: '',
      area_name: '',
    })
  }

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormError(null)
    
    try {
      if (mode === 'create') {
        await createAreaMutation.mutateAsync(formData)
      } else {
        if (!editingAreaId) {
          setFormError('Please close the dialog and select an area to edit again.')
          return
        }
        await updateAreaMutation.mutateAsync({
          id: editingAreaId,
          data: formData,
        })
      }
      handleFormClose()
    } catch (error) {
      setFormError(
        typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'Unable to save the area. Please try again.'
      )
    }
  }

  const handleInputChange = (field: keyof CreateAreaForm) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const getBookerName = (bookerId: any) => {
    if (typeof bookerId === 'string') {
      const booker = bookers.find((b: any) => b._id === bookerId)
      return booker?.name || 'Unknown'
    }
    return bookerId?.name || 'Unknown'
  }

  const columns: Column<Area>[] = [
    {
      id: 'area_name',
      label: 'Area Name',
      minWidth: 200,
    },
    {
      id: 'booker_id',
      label: 'Booker',
      minWidth: 150,
      render: (value) => getBookerName(value),
    },
    {
      id: 'is_active',
      label: 'Status',
      minWidth: 100,
      render: (value) => value === false ? 'Disabled' : 'Active',
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 120,
      render: (value) => format(new Date(value), 'MMM dd, yyyy'),
    },
    {
      id: 'updatedAt',
      label: 'Updated',
      minWidth: 120,
      render: (value) => format(new Date(value), 'MMM dd, yyyy'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center',
      render: (_, row) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, row)}
          disabled={row.is_active === false}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Areas</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingAreaId(null)
            setFormError(null)
            setMode('create')
            setFormOpen(true)
          }}
        >
          Add Area
        </Button>
      </Box>

      <Paper>
        <Table
          columns={columns}
          data={areas}
          pagination={{
            page,
            rowsPerPage,
            total: areasData?.info?.total || areas.length,
            onPageChange: setPage,
            onRowsPerPageChange: setRowsPerPage,
          }}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditArea}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteArea} sx={{ color: 'warning.main' }}>
          <DisableIcon sx={{ mr: 1 }} />
          Disable
        </MenuItem>
      </Menu>

      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {mode === 'create' ? 'Create New Area' : 'Edit Area'}
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Booker</InputLabel>
                  <Select
                    value={formData.booker_id}
                    onChange={handleInputChange('booker_id')}
                    label="Booker"
                  >
                    {bookers.map((booker: any) => (
                      <MenuItem key={booker._id} value={booker._id}>
                        {booker.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Area Name"
                  value={formData.area_name}
                  onChange={handleInputChange('area_name')}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFormClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createAreaMutation.isPending || updateAreaMutation.isPending}
            >
              {mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default AreaListPage
